import AirDatepicker from 'air-datepicker';
import localeEn from 'air-datepicker/locale/en';

import g from '#core/global';
import load from '#core/load';
import filter from '#component/filter/filter';

const any_field_$ = 'input';
const show_invalid_c = 'show-invalid';
const date_field_$ = '.field.\\:type-datepicker input';
const datepicker_hide_delay = 250;
const state_source_select_$ = 'fieldset.js-state-source select';
const state_target_select_$ = 'div.js-state-target select';
const state_abbrev_target_select_$ = 'div.js-state-abbrev-target select';

let has_datepicker = false;
let $datepickers = null;
let has_state_select_sync = false;

const module = {
	date_field_$: date_field_$,

	init() {
		if (load.ran('field')) return false;

		load.content(() => {
			has_datepicker = assign_elements();
			if (!has_datepicker) return false;
		});

		load.all(() => {
			has_datepicker = assign_elements();
			if (!has_datepicker) return false;
		});
	},
};

export default module;
module.init();

/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_datepicker) return true;

	// Basic validation on blur for all inputs
	document.querySelectorAll(any_field_$).forEach($field => {
		$field.addEventListener(g.blur_e, () => {
			validate_field($field);
		});
	});

	// Init datepickers
	$datepickers = document.querySelectorAll(date_field_$);
	if ($datepickers) {
		$datepickers.forEach($datepicker => {
			init_datepicker($datepicker);
		});
	}

	// Keep "State (sanitized)" select in sync with Address State select
	init_state_select_sync();

	return true;
}

/**
 * Basic validation helper
 * - adds `show-invalid` if empty or fails native validity
 * - removes it when valid
 * @param {HTMLInputElement} $field
 */
function validate_field($field) {
	const value = $field.value ? $field.value.trim() : '';

	let is_valid = true;

	if (typeof $field.checkValidity === 'function') {
		is_valid = $field.checkValidity() && value !== '';
	} else {
		is_valid = value !== '';
	}

	if (!is_valid) {
		$field.classList.add(show_invalid_c);
	} else {
		$field.classList.remove(show_invalid_c);
	}
}

/**
 * Initialize datepicker
 * @param {HTMLInputElement} $datepicker
 */
function init_datepicker($datepicker) {
	new AirDatepicker($datepicker, {
		locale: localeEn,

		onSelect: ({ formattedDate, datepicker }) => {
			// Explicitly set the input value when a date is selected
			if (formattedDate) {
				$datepicker.value = formattedDate;

				// Trigger events so anything listening picks up the new value
				$datepicker.dispatchEvent(new Event('input', { bubbles: true }));
				$datepicker.dispatchEvent(new Event('change', { bubbles: true }));

				// Re-validate now that it has a value
				validate_field($datepicker);
			}

			setTimeout(() => {
				document.dispatchEvent(new CustomEvent(filter.request_e, {
					detail: {
						$source: $datepicker,
					},
				}));

				datepicker.hide();
			}, datepicker_hide_delay);
		},
	});
}

/**
 * Keep two state selects in sync by visible option label.
 * This is used when a "sanitized" state field should mirror the Address State selection.
 */
function init_state_select_sync() {
	if (has_state_select_sync) return true;
	has_state_select_sync = true;

	document.addEventListener(g.change_e, (e) => {
		if (e.target && e.target.matches && e.target.matches(state_source_select_$)) {
			sync_state_select_by_text();
			sync_state_abbrev_by_text();
		}
	});

	sync_state_select_by_text();
	sync_state_abbrev_by_text();

	return true;
}

function sync_state_select_by_text() {
	const $src = document.querySelector(state_source_select_$);
	const $tgt = document.querySelector(state_target_select_$);
	if (!$src || !$tgt) return false;

	const src_option = $src.options[$src.selectedIndex];
	const src_text = (src_option && src_option.text ? src_option.text : '').trim();
	if (!src_text) return false;

	let match_value = null;
	for (const opt of $tgt.options) {
		if ((opt.text || '').trim() === src_text) {
			match_value = opt.value;
			break;
		}
	}

	if (!match_value) return false;

	$tgt.value = match_value;
	$tgt.dispatchEvent(new Event(g.change_e, { bubbles: true }));
	return true;
}

function sync_state_abbrev_by_text() {
	const $src = document.querySelector(state_source_select_$);
	const $tgt = document.querySelector(state_abbrev_target_select_$);
	if (!$src || !$tgt) return false;

	const src_option = $src.options[$src.selectedIndex];
	const src_text = (src_option && src_option.text ? src_option.text : '').trim();
	if (!src_text) return false;

	let match_value = null;
	for (const opt of $tgt.options) {
		if ((opt.text || '').trim() === src_text) {
			match_value = opt.value;
			break;
		}
	}

	if (!match_value) return false;

	$tgt.value = match_value;
	$tgt.dispatchEvent(new Event(g.change_e, { bubbles: true }));
	return true;
}
